import { useCallback, useEffect, useState } from "react";
import useTopicHandler from "../../../hooks/useTopicHandler"
import { useConfigsDataContext } from "../../../providers/ConfigsDataContextProvider";
import { useBreadCrumbsContext } from "../../../providers/BreadCrumbsContextProvider";
import { getFolderName } from "../../../hooks/useConfigsNavigator";
import { getPath, useConfigPathContext } from "../../../providers/ConfigPathContextProvider";
import useQueueHandler from "../../../hooks/useQueueHandler";

export default function useFolderListener() {  
  // TODO: replace setCreatingInProgress with a general updateKInfo()
  const {setConfigPath}                                               = useConfigPathContext();
  const {getNode, createFolder, renameFolder, setCreatingInProgress, 
         deleteFolder, updateSdkData,         
         tagFolderCreatedError, tagFolderRenamedError,  resetFolderRenamedError,
         tagCreateFolderInProgress, resetFolderKInfo, setFolderEdit
        }                                                             = useConfigsDataContext();
  const {topicData:folderCreated}                                     = useTopicHandler('config-folder-created');
  const {queueData:folderCreatedError}                                = useQueueHandler('config-folder-created');

  const {topicData:folderRenamed}                                     = useTopicHandler('config-folder-renamed');
  const {queueData:folderRenamedError}                                = useQueueHandler('config-folder-renamed');

  const {topicData:folderDeleted}                                     = useTopicHandler('config-folder-deleted');
  // SDK GENERATED
  const {topicData:sdkGeneratedData}                                  = useTopicHandler('sdk-generated');

  const [folderChanged, setFolderChanged] = useState(false);
  //const flagFolderChanged = useCallback(() => setFolderChanged(prev => !prev), []);
  const flagFolderChanged = useCallback(()=>{
    //console.log('------[  fireFolderChanged   ]------');
    setFolderChanged(Date.now());
  }, []);

  const {setConfigCrumbs} = useBreadCrumbsContext();

  //------[   Clear ErrorFolderCreated   ]------
  //
  const clearErrorFolderCreated = useCallback(async (folderCreatedError) => {
    return tagFolderCreatedError(folderCreatedError, false)
              .then(flagFolderChanged);
  }, [tagFolderCreatedError, flagFolderChanged]);

  const clearErrorFolderRenamed = useCallback(async (folderRenamedError) => {
    return resetFolderRenamedError(folderRenamedError)
              .then(flagFolderChanged);
  }, [resetFolderRenamedError, flagFolderChanged]);

  const clearFolderKInfo = useCallback(async (basePath, folderName) => {
    return resetFolderKInfo(basePath, folderName)
              .then(flagFolderChanged);
  }, [resetFolderKInfo, flagFolderChanged]);


  const tagFolderEdit = useCallback(async (parentPath, folderName) => {
    return setFolderEdit(parentPath, folderName)
            .then(flagFolderChanged);
  }, [setFolderEdit, flagFolderChanged]);

  /*------[  Effect  FolderCreatedError   ]------
  |
    .errorTag(errorTag.getTagId())
    .basePath(basePath)
    .oldKey(oldKey)
    .newKey(newKey)
    .val(val)
    .title(title)
    .cause(ex.getMessage())
    .message(messageTitle)
    .logFinder(finderId)
    .suggestion(suggestionToUser)
    .build();
  |
  |*/
  useEffect(()=>{
    if (folderCreatedError) {
      // const {newKey:folderName} = folderCreatedError;
      // console.log('[useFolderListener Effect folderCreatedError] [folderName: %s]', folderName);
      tagFolderCreatedError(folderCreatedError)
        .then(flagFolderChanged);
    }
    
  },[folderCreatedError, tagFolderCreatedError, flagFolderChanged]);

  useEffect(()=>{
    if (folderRenamedError) {
      tagFolderRenamedError(folderRenamedError)
        .then(flagFolderChanged);
    }
  }, [folderRenamedError, tagFolderRenamedError, flagFolderChanged]);

  //------[   Rebuild Crumbs   ]------
  //
  const rebuildCrumbs = useCallback((parentPath, oldFolderName, newFolderName = '') => {
    
    setConfigCrumbs( prev => {

      const freshCrumbs = {};
      const oldPath = `${parentPath}['${oldFolderName}']`;
      const newPath = `${parentPath}['${newFolderName}']`;
      const folderDeletedMode = newFolderName === '';
      
      if (prev) {
        Object.keys(prev).forEach(crumbPath => {
          // Accurately remove the entire origPath from the crumb and replace it with the new path. use startsWith, as that's the meaning of "path".
          if (crumbPath.startsWith(oldPath)) {
            if (folderDeletedMode) {
              if (!freshCrumbs[parentPath]) {
                freshCrumbs[parentPath] = prev[parentPath] ? prev[parentPath] : getFolderName(parentPath);
              }
            }
            else { // Rename Mode
              const fixedPath = crumbPath.replace(oldPath, newPath);
              freshCrumbs[fixedPath] = getFolderName(fixedPath);
            }
          }
          else {
            // Crumb remains as is, but keep it only if it's a valid path. (still exists)
            const node = getNode(crumbPath);
            if (Boolean(node)) {
              freshCrumbs[crumbPath] = getFolderName(crumbPath);
            }
          }
        });

        return freshCrumbs;
      }
      else {
        return prev;
      }
    });

  }, [setConfigCrumbs, getNode]);


  //------[  Effect  Folder Created  ]------
  //
  useEffect(()=>{
    if (folderCreated) {
      // console.log('[useFolderListener folderCreated]');
      
      const folderName = folderCreated.folderName;
      const parent = {...getNode(folderCreated.path)};

      if (parent) {
        if (!parent[folderName]) { // Created by another team member
          createFolder(folderCreated)
            .then(flagFolderChanged)
        }
        else { // Submitted by this user, so expecting the folder status: "createInProgress"          
          setCreatingInProgress(folderCreated.path, folderName, false)
            .then(flagFolderChanged);
        }
      }
    }
  }, [folderCreated, createFolder, getNode, setCreatingInProgress, flagFolderChanged]);


  //------[  Effect  Sdk Generated  ]------
  //
  useEffect(()=>{
    if (sdkGeneratedData) {
      // console.log('[useFolderListener sdkGenerated]');
      updateSdkData(sdkGeneratedData)
        .then(flagFolderChanged) // More precisely, should be sdkDataChanged, but we'll do fine for now with flagFolderChanged as it's like a special folder afterall..
        .then(window.gtag_report_sdk_conversion());
    }
  },[sdkGeneratedData, updateSdkData, flagFolderChanged]);


  //------[  Effect  Folder Deleted  ]------
  //
  useEffect(()=>{
    if (folderDeleted) {
      // console.log('[useFolderListener folderDeleted]');
      deleteFolder(folderDeleted)
        .then(() => rebuildCrumbs(folderDeleted.basePath, folderDeleted.folderName))
        .then(()=> setConfigPath( prev => {
          const deletedPath = getPath(folderDeleted.basePath, folderDeleted.folderName);
          return prev.startsWith(deletedPath) ? folderDeleted.basePath : prev;
        }))
        .then(flagFolderChanged)
    }
  }, [folderDeleted, deleteFolder, rebuildCrumbs, flagFolderChanged, setConfigPath]);


  //------[  Effect  Folder Renamed  ]------
  //
  useEffect(()=>{
    if (folderRenamed) {
      // console.log('[useFolderListener folderRenamed]');
      renameFolder(folderRenamed)
        .then( result => {
            if (result === 'Renamed') {
              rebuildCrumbs(folderRenamed.basePath, folderRenamed.oldFolderName, folderRenamed.newFolderName);

              setConfigPath(prev => {
                              const pathToDelete  = getPath(folderRenamed.basePath, folderRenamed.oldFolderName);
                              const pathToInsert  = getPath(folderRenamed.basePath, folderRenamed.newFolderName);
                              return prev.startsWith(pathToDelete) ? prev.replace(pathToDelete, pathToInsert) : prev;
                            });
                            
              flagFolderChanged();
            }
          });
    }
  }, [folderRenamed, renameFolder, rebuildCrumbs, flagFolderChanged, setConfigPath]);

  return { 
    folderChanged,
    clearErrorFolderRenamed, 
    tagFolderCreatedError,
    tagFolderRenamedError,
    tagCreateFolderInProgress,
    clearErrorFolderCreated,
    clearFolderKInfo,
    tagFolderEdit
   }
}
